import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { emptyRole, Role } from 'src/app/models/data/role';
import { SystemCapabilityItem } from 'src/app/models/data/systemCapability';
import { Mode } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import { UserSessionService } from 'src/app/services/user-session.service';
import { AUDIT_LOG_COLUMN_PROPERTY } from 'src/app/app.constants';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { RoleDetailCapabilitiesTableComponent } from './role-detail-capabilities-table/role-detail-capabilities-table.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { RoleDetailUserTableComponent } from './role-detail-user-table/role-detail-user-table.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';

@Component({
	selector: 'app-role-detail',
	templateUrl: './role-detail.component.html',
	styleUrls: ['./role-detail.component.scss'],
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		RoleDetailCapabilitiesTableComponent,
		BreadcrumbLabelDirective,
		RoleDetailUserTableComponent,
		AuditLogListComponent,
	],
})
export class RoleDetailComponent implements OnInit {
	roleId: string;
	role: Role;
	mode: Mode;
	companyId = '';
	companyType?: 'advanced' | 'care';
	routeCompanyId: string | undefined =
		this.activatedRoute.snapshot.params.companyId;
	systemCapabilityItems: SystemCapabilityItem[] = [];

	capabilitiesBreadcrumbLabel = '';
	userTableBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';

	canAccessAuditLogs: boolean = false;

	hiddenAuditLogColumns = [AUDIT_LOG_COLUMN_PROPERTY];

	constructor(
		private readonly activatedRoute: ActivatedRoute,
		private readonly router: Router,
		private readonly apiService: ApiService,
		private readonly userSessionService: UserSessionService,
		private readonly systemCapabilityService: SystemCapabilityService
	) {
		this.roleId = this.activatedRoute.snapshot.params.id;
		this.role =
			this.router.lastSuccessfulNavigation?.extras?.state?.role ?? emptyRole();
		this.mode =
			this.router.lastSuccessfulNavigation?.extras?.state?.mode ??
			Mode.viewOnly;
	}

	get roleName(): string {
		return this.role?.name ?? '';
	}

	updateBreadcrumbLabels(): void {
		this.capabilitiesBreadcrumbLabel = `${this.role?.name}`;
		this.userTableBreadcrumbLabel = `${this.role?.name}`;
		this.auditLogBreadcrumbLabel = `${this.role?.name}`;
	}

	async ngOnInit(): Promise<void> {
		const { role, systemCapabilities } = await this.apiService.getRoleById(
			this.roleId
		);
		this.role = role;
		this.systemCapabilityItems = systemCapabilities;

		// Backend determines if audit logs can be viewed based on:
		// 1. Requesting user has audit capability, AND
		// 2. Either requesting user is Sentrilock OR role belongs to requesting user's company
		this.canAccessAuditLogs = this.role?.canViewAuditLogs ?? false;

		// Use the role's owner entity ID to get the correct company information
		// If routeCompanyId is provided (from URL), use that, otherwise use the user's assigned company ID
		this.companyId =
			this.routeCompanyId ??
			this.userSessionService.userSession.assignedRole?.entityRelationship
				?.parentEntityId ??
			'';

		// Fetch company information to get company type
		if (this.companyId) {
			try {
				const companyData = await this.apiService.getCompanyById(
					this.companyId
				);
				this.companyType = companyData?.company?.companyType;
			} catch (error) {
				console.error('Error fetching company information:', error);
			}
		}

		this.updateBreadcrumbLabels();
	}
}
